import cx from "classnames";
import { DmmHeader } from "components/DmmHeader";
import { Modal } from "components/Modal";
import { graphql, useStaticQuery } from "gatsby";
import { StaticImage } from "gatsby-plugin-image";
import { Link, useI18next } from "gatsby-plugin-react-i18next";
import { isDMM } from "libs/dmm";
import usePlatform from "hooks/usePlatform";
import { useDMMPopupState } from "hooks/useDMMPopupState";
import React, { useEffect, useState } from "react";

import { Menu, Transition } from "@headlessui/react";
import { useMediaQuery, useToggle } from "@react-hookz/web";
import { useScroll } from "@use-gesture/react";
import * as NavigationMenu from "@radix-ui/react-navigation-menu";
import { ReactComponent as ArrowRightIcon } from "icons/arrow-right-o.svg";
import { getNewsUrl } from "libs/news";

const LANGUAGES = [
    { display: "繁", language: "zh-tw" },
    { display: "简", language: "zh-cn" },
    { display: "EN", language: "en" },
    { display: "日", language: "ja" },
    { display: "한", language: "ko" },
];

const Logo = React.memo((props: { language: string }) => {
    switch (props.language) {
        case "zh-cn":
            return <StaticImage alt="logo" src="../images/logo-s-zh.png" />;
        case "ja":
            return <StaticImage alt="logo" src="../images/logo-s-ja.png" />;
        case "ko":
            return <StaticImage alt="logo" src="../images/logo-s-ko.png" />;
        case "en":
            return <StaticImage alt="logo" src="../images/logo-s-en.png" />;
        default:
            return <StaticImage alt="logo" src="../images/logo-s-zh.png" />;
    }
});

const DownloadButton = React.memo((props: { language: string }) => {
    const imageProps = {
        alt: "Download",
        className: "!hidden xl:!block",
        width: 169,
    };
    const mobileImageProps = {
        alt: "Download",
        className: "xl:!hidden",
        width: 140,
    };
    switch (props.language) {
        case "zh-tw":
            return (
                <>
                    <StaticImage
                        {...imageProps}
                        src="../images/btn-dl-zh.png"
                    />
                    <StaticImage
                        {...mobileImageProps}
                        src="../images/btn-dl-m-2x-zh.png"
                    />
                </>
            );
        case "zh-cn":
            return (
                <>
                    <StaticImage
                        {...imageProps}
                        src="../images/btn-dl-cn.png"
                    />
                    <StaticImage
                        {...mobileImageProps}
                        src="../images/btn-dl-m-2x-cn.png"
                    />
                </>
            );
        case "ja":
            return (
                <>
                    <StaticImage
                        {...imageProps}
                        src="../images/btn-dl-ja.png"
                    />
                    <StaticImage
                        {...mobileImageProps}
                        src="../images/btn-dl-m-2x-ja.png"
                    />
                </>
            );
        case "ko":
            return (
                <>
                    <StaticImage
                        {...imageProps}
                        src="../images/btn-dl-ko.png"
                    />
                    <StaticImage
                        {...mobileImageProps}
                        src="../images/btn-dl-m-2x-ko.png"
                    />
                </>
            );
        default:
            return (
                <>
                    <StaticImage
                        {...imageProps}
                        src="../images/btn-dl-en.png"
                    />
                    <StaticImage
                        {...mobileImageProps}
                        src="../images/btn-dl-m-2x-en.png"
                    />
                </>
            );
    }
});

export const Header = () => {
    const { language } = useI18next();
    const isMobile = useMediaQuery("(max-width: 1280px)", true);
    const [menuOpen, setMenuOpen] = useState(false);
    const [languageOpen, setLanguageOpen] = useState(false);
    const { isMobilePlatform } = usePlatform()

    const [isTop, toggleBg] = useToggle(true);
    const { setIsOpen } = useDMMPopupState();

    const handleMenuClose = () => setMenuOpen(false);
    const handleLanguageClose = () => setLanguageOpen(false);

    useScroll(
        ({ xy: [, y] }) => {
            if ([y === 0 && isTop, y > 0 && !isTop].some(Boolean)) return;
            toggleBg();
        },
        { target: typeof window !== "undefined" ? window : undefined }
    );

    return (
        <>
            <header
                className={cx("fixed top-0 inset-x-0 z-20 transition", {
                    "bg-[#0c0b0b]/80": !isTop,
                })}
            >
                {isDMM && <DmmHeader />}
                <div className="flex p-4 xl:py-0 xl:h-[66px] xl:relative xl:items-center xl:px-5 ">
                    {!isDMM && (
                        <button
                            type="button"
                            className="xl:hidden"
                            onClick={() => setLanguageOpen(true)}
                        >
                            <StaticImage
                                src="../images/btn-lang-m-2x.png"
                                alt="language"
                                loading="eager"
                                width={44}
                            />
                        </button>
                    )}
                    <Link to="/" className="hidden xl:block">
                        <Logo language={language} />
                    </Link>
                    <div className="hidden mx-auto transform 2xl:absolute 2xl:-translate-x-1/2 2xl:-translate-y-1/2 left-1/2 top-1/2 xl:block">
                        <Nav onClick={handleMenuClose} />
                    </div>
                    {isDMM ? (
                        <button
                            type="button"
                            className="ml-auto"
                            onClick={() => {
                                if(isMobilePlatform){
                                    window.open("https://games.dmm.co.jp/detail/riseoferos")}
                                else {
                                    setIsOpen(true)
                                }
                            }}
                        >
                            <DownloadButton language="ja" />
                        </button>
                    ) : (
                        <NavigationMenu.Root className="ml-auto relative">
                            <NavigationMenu.List>
                                <NavigationMenu.Item>
                                    <NavigationMenu.Trigger className="focus:outline-0 mt-0.5 xl:mt-1.5">
                                        <DownloadButton language={language} />
                                    </NavigationMenu.Trigger>
                                    <NavigationMenu.Content className="space-y-1.5 mt-1.5 xl:mt-0">
                                        {[
                                            {
                                                text: "EROLABS",
                                                href: "https://bit.ly/3d9R1Mk",
                                            },
                                            {
                                                text: "STEAM",
                                                href: "https://store.steampowered.com/app/2346550/Rise_of_Eros/",
                                            },
                                        ].map(({ text, href }) => (
                                            <NavigationMenu.Link
                                                key={text}
                                                href={href}
                                                rel="noopener noreferrer"
                                                target="_blank"
                                                className="grid ml-px w-[138px] h-8 xl:w-[169px] xl:h-[40px] xl:ml-0"
                                            >
                                                <StaticImage
                                                    src="../images/btn-box-s@3x.png"
                                                    alt=""
                                                    loading="eager"
                                                    className="col-span-full row-span-full"
                                                />
                                                <span className="col-span-full row-span-full flex items-center px-3 text-white text-xs tracking-widest z-0">
                                                    <ArrowRightIcon
                                                        width={21}
                                                        height={21}
                                                    />
                                                    <span className="flex-grow text-center">
                                                        {text}
                                                    </span>
                                                </span>
                                            </NavigationMenu.Link>
                                        ))}
                                    </NavigationMenu.Content>
                                </NavigationMenu.Item>
                            </NavigationMenu.List>
                            <NavigationMenu.Viewport className="absolute top-full left-0 data-[state=open]:animate-in data-[state=open]:fade-in data-[state=closed]:animate-out data-[state=closed]:fade-out" />
                        </NavigationMenu.Root>
                    )}
                    <button
                        type="button"
                        className="ml-1.5 xl:hidden"
                        onClick={() => setMenuOpen(true)}
                    >
                        <StaticImage
                            src="../images/btn-menu-m-2x.png"
                            alt="menu"
                            loading="eager"
                            width={44}
                        />
                    </button>
                    {!isDMM && (
                        <Menu
                            as="div"
                            className="hidden xl:inline-block xl:ml-1.5"
                        >
                            <div>
                                <Menu.Button>
                                    <StaticImage
                                        src="../images/btn-lang.png"
                                        alt="language"
                                        loading="eager"
                                        width={169}
                                    />
                                </Menu.Button>
                            </div>
                            <Transition
                                enter="transition ease-out duration-100"
                                enterFrom="transform-opacity opacity-0"
                                enterTo="transform-opacity opacity-100"
                                leave="transition ease-in duration-75"
                                leaveFrom="transform-opacity opacity-100"
                                leaveTo="transform-opacity opacity-0"
                            >
                                <Menu.Items className="absolute grid mt-2 right-6 focus:outline-none">
                                    <StaticImage
                                        className="col-span-full row-span-full -z-10"
                                        src="../images/btn-lang-menu-bg.png"
                                        alt="background"
                                    />
                                    <div className="grid grid-rows-5 px-1 py-6 col-span-full row-span-full">
                                        {LANGUAGES.map((props) => (
                                            <Menu.Item key={props.display}>
                                                <div className="text-center">
                                                    <LanguageItem
                                                        onClick={
                                                            handleLanguageClose
                                                        }
                                                        {...props}
                                                    />
                                                </div>
                                            </Menu.Item>
                                        ))}
                                    </div>
                                </Menu.Items>
                            </Transition>
                        </Menu>
                    )}
                </div>
            </header>
            <Modal
                showClose
                open={menuOpen && !!isMobile}
                onClose={handleMenuClose}
            >
                <Nav onClick={handleMenuClose} />
            </Modal>
            <Modal
                showClose
                open={languageOpen && !!isMobile}
                onClose={handleLanguageClose}
            >
                <nav>
                    <ul className="flex flex-col items-center space-y-4">
                        {LANGUAGES.map((props) => (
                            <li key={props.display}>
                                <LanguageItem
                                    onClick={handleLanguageClose}
                                    {...props}
                                />
                            </li>
                        ))}
                    </ul>
                </nav>
            </Modal>
        </>
    );
};

const LanguageItem: React.FC<
    typeof LANGUAGES[number] & { onClick: () => void }
> = ({ display, language, onClick }) => {
    const { changeLanguage } = useI18next();
    const handleClick = async () => {
        await changeLanguage(language);
        onClick();
    };

    return (
        <button
            type="button"
            className="text-xl text-white"
            onClick={handleClick}
        >
            {display}
        </button>
    );
};

const Nav: React.FC<{ onClick: () => void }> = ({ onClick }) => {
    const { t, language } = useI18next();
    const data = useStaticQuery<Queries.NavQuery>(graphql`
        query Nav {
            characters: allMarkdownRemark(
                filter: {
                    frontmatter: { type: { eq: "character" } }
                    fields: { language: { eq: "en" } }
                }
                limit: 1
            ) {
                edges {
                    node {
                        frontmatter {
                            slug
                        }
                    }
                }
            }
        }
    `);
    const pages = [
        { display: t("nav.home"), link: "/" },
        { display: t("nav.news"), link: getNewsUrl(language), isExternal: true },
        {
            display: t("nav.characters"),
            link:
                "/characters/" +
                data.characters.edges[0].node.frontmatter?.slug,
        },
        { display: t("nav.boudoir"), link: "/boudoir" },
        isDMM
            ? { display: "お問合せ(メール)", link: "mailto:ask-riseoferos@pinkcore.com", isExternal: true }
            : { display: t("nav.faq"), link: "/faq" },
    ];

    return (
        <nav>
            <ul className="flex flex-col items-center space-y-4 xl:flex-row xl:space-y-0">
                {pages.map(({ display, link, isExternal }, i) => (
                    <li key={link} className="text-white">
                        {i > 0 && (
                            <span className="hidden mx-6 xl:inline-block">
                                /
                            </span>
                        )}
                        {isExternal ? (
                            <a
                                href={link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xl xl:hover:text-primary"
                                onClick={onClick}
                            >
                                {display}
                            </a>
                        ) : (
                            <Link
                                to={link}
                                className="text-xl xl:hover:text-primary"
                                activeClassName="xl:text-primary"
                                onClick={onClick}
                            >
                                {display}
                            </Link>
                        )}
                    </li>
                ))}
            </ul>
        </nav>
    );
};
